import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useHistory } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import eventApi from '_api/event';
import {
  getUserInfo,
  getEventHostingDraft,
  setEventHostingDraft,
  getDeviceUser,
} from '_utils/localData';
import {
  EVENT_STATUS_INACTIVE,
  EVENT_STATUS_ACTIVE,
  PAYNOW,
  NOT_PAYNOW,
  PAYABLE,
  NOT_PAYABLE,
  SPLIT_CASE_CONDITION_FULL,
  SPLIT_CASE_CONDITION_PARTIAL,
  PHONE,
} from '_utils/constant';
import { convertToGMT0 } from '_utils/function';
import { clearAllEvents, clearMyEvents } from '_redux/modules/event';
import { appendNewEventToGroup } from '_redux/modules/group';
import clearDraftData, { useClearDraftOnLeave } from './clearDraftData';
import useCountStep from './useCountStep';
import useFetchEventDetail from './useFetchEventDetail';
import { fullEventSchema } from '../schemas/eventSchemas';
import { DEFAULT_EVENT_FORM_VALUES } from '../constants/eventForm';

export default function useEventHosting() {
  const history = useHistory();
  const dispatch = useDispatch();
  const { addToast } = useToasts() || {};
  const [draft] = useState(() => getEventHostingDraft());
  const eventId = new URLSearchParams(history.location.search).get('id');
  const fromReHost = new URLSearchParams(history.location.search).get('fromReHost') === '1';
  const isRehostMode = Boolean(fromReHost && eventId);
  const isEditMode = Boolean(eventId && !fromReHost);

  const restoredStep = draft?.currentStep >= 1 && draft?.currentStep <= 3 ? draft.currentStep : 1;
  const { currentStep, nextStep, prevStep, goToStep } = useCountStep(restoredStep);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useClearDraftOnLeave();

  useEffect(() => {
    document.body.classList.add('event-hosting-active');
    const mainScreen = document.querySelector('.mains-screen');
    if (mainScreen) {
      mainScreen.classList.add('event-hosting-active');
    }
    return () => {
      document.body.classList.remove('event-hosting-active');
      if (mainScreen) {
        mainScreen.classList.remove('event-hosting-active');
      }
    };
  }, []);

  const methods = useForm({
    mode: 'onTouched',
    resolver: yupResolver(fullEventSchema),
    defaultValues: draft?.formValues || DEFAULT_EVENT_FORM_VALUES,
  });

  useEffect(() => {
    const subscription = methods.watch((values) => {
      setEventHostingDraft({ formValues: values, currentStep });
    });
    return () => subscription.unsubscribe();
  }, [methods, currentStep]);

  useFetchEventDetail({
    eventId,
    reset: methods.reset,
    addToast,
    fromReHost: isRehostMode,
  });

  const submitEvent = async ({ isDraft = false } = {}) => {
    const formData = methods.getValues();
    const userInfo = getUserInfo();

    const rawGroupId = formData.groupId;
    const groupId =
      rawGroupId?.id ||
      rawGroupId?.gid ||
      rawGroupId?.value ||
      (typeof rawGroupId === 'string' ? rawGroupId : '');

    const closingTimeFormatted = formData.closingDate
      ? `${moment(formData.closingDate).format('DD-MM-YYYY')} ${formData.timeTo || '23:59'}`
      : '';

    const pickupTimeFormatted = formData.collectionDate
      ? `${moment(formData.collectionDate).format('DD-MM-YYYY')} ${formData.timeFrom || '10:00'}`
      : '';

    const closingTime = closingTimeFormatted ? convertToGMT0(closingTimeFormatted) : '';
    const pickupTime = pickupTimeFormatted ? convertToGMT0(pickupTimeFormatted) : '';

    let pickupDuration = 4 * 3600;
    if (formData.timeFrom && formData.timeTo) {
      const start = moment(formData.timeFrom, 'HH:mm');
      const end = moment(formData.timeTo, 'HH:mm');
      const diffInSeconds = end.diff(start, 'seconds');
      if (diffInSeconds > 0) {
        pickupDuration = diffInSeconds;
      }
    }

    const products = (formData.items || []).map((item) => {
      const p = {
        name: item.name,
        description: item.description || null,
        price: Number(item.price || 0),
        maxQuantity: Number(item.maxQuantity || 1000),
        minOrderQty: Number(item.minOrderQty || 1),
        defaultQuantity: Number(item.defaultQuantity || 0),
        limitPerOrder: Number(item.limitPerOrder || 1000),
        photoUrls: item.image
          ? [typeof item.image === 'string' ? item.image : item.image.url]
          : [],
        subItems: [],
      };

      if (
        item.splitCasePurchase &&
        (Number(item.splitCasePurchase.itemsPerCase) > 0 || item.splitCasePurchase.name)
      ) {
        const sc = item.splitCasePurchase;
        const isFull = Boolean(
          sc.isFullCase ||
            sc.fulfillCondition === SPLIT_CASE_CONDITION_FULL ||
            sc.fulfilmentOption === 'full'
        );

        p.pSplitCasePurchase = true;
        p.splitCasePurchase = {
          name: sc.name || item.name || 'Split case',
          itemsPerCase: Number(sc.itemsPerCase || 0),
          costPerItem: Number(sc.costPerItem || 0),
          minItemOrder: Number(sc.minItemOrder || 1),
          isFullCase: isFull,
          fulfillCondition: isFull ? SPLIT_CASE_CONDITION_FULL : SPLIT_CASE_CONDITION_PARTIAL,
          description: item.description || null,
          photoUrl:
            typeof item.image === 'string' ? item.image : item.image?.url || '',
          acknowledgementRequiredPayAfter: isFull ?
            Boolean(sc.acknowledgementRequiredPayAfter) : false,
          enabled: true,
        };
      }

      if (Array.isArray(item.subItems) && item.subItems.length > 0) {
        p.subItems = item.subItems.map((group) => ({
          type: group.type || group.title || '',
          selectionMode: group.selectionMode || 'single',
          required: group.required ? 1 : 0,
          trackInventory: Boolean(group.trackInventory),
          list: (group.list || group.choices || []).map((c) => {
            const choice = {
              name: c.name,
              price: Number(c.price || 0),
            };
            if (group.trackInventory && c.maxQty !== undefined && c.maxQty !== null && c.maxQty !== '') {
              choice.maxQty = Number(c.maxQty);
            }
            return choice;
          }),
        }));
      }

      if (isEditMode && typeof item.id === 'string' && item.id.length > 5) {
        p.id = item.id;
      }

      return p;
    });

    const photoUrls = (formData.banners || [])
      .map((b) => (typeof b === 'string' ? b : b?.url))
      .filter(Boolean);

    const rawLinks = Array.isArray(formData.extraFields)
      ? formData.extraFields
      : (formData.extraFields?.list || []);

    const linksList = rawLinks
      .filter((link) => link.name && (link.value || link.url))
      .map((link) => ({
        name: link.name,
        value: link.value || link.url,
      }));

    const enableSafeLinks = Boolean(formData.enableSafeLinks);

    const postData = {
      groupId,
      title: formData.title || '',
      description: formData.description ? formData.description.trim() : null,
      closingTime,
      pickupTime,
      pickupDuration,
      pickupAddress: formData.location || null,
      photoUrls,
      paynow: formData.payAfterClosing ? NOT_PAYNOW : PAYNOW,
      payable: formData.payAfterClosing ? NOT_PAYABLE : PAYABLE,
      payTogether: formData.payTogether ? 1 : 0,
      payAfterClosing: formData.payAfterClosing ? 1 : 0,
      adminCost: Number(formData.adminCost || 0),
      orderDiscountEnabled: Boolean(formData.orderDiscountEnabled),
      orderDiscount: formData.orderDiscountEnabled ? formData.orderDiscount : null,
      termCond: formData.importantNotes || formData.termCond || null,
      bookingAddressRequired: Boolean(formData.bookingAddressRequired),
      isDraft: Boolean(isDraft),
      status: isDraft ? EVENT_STATUS_INACTIVE : EVENT_STATUS_ACTIVE,
      extraFields: {
        enableSafeLinks,
        list: linksList,
      },
      createdUserPhotoUrl: userInfo?.photoUrl || null,
      deliveryStatus: 0,
      products,
    };

    if (isEditMode) {
      postData.id = eventId;
    }

    setIsSubmitting(true);
    try {
      const res = isEditMode
        ? await eventApi.updateEvent(postData)
        : await eventApi.createEvent(postData);
      const { msgCode, msgResp } = res || {};

      if (msgCode === 99999) {
        if (addToast) {
          addToast(
            msgResp || `Failed to ${isEditMode ? 'update' : isRehostMode ? 're-host' : 'create'} event`,
            {
              appearance: 'error',
              autoDismiss: true,
            }
          );
        }
        setIsSubmitting(false);
        return;
      }

      if (dispatch) {
        dispatch(clearAllEvents());
        dispatch(clearMyEvents());
        if (msgResp) {
          dispatch(appendNewEventToGroup(msgResp));
        }
      }

      clearDraftData();

      if (addToast) {
        let successMessage = '';
        if (isRehostMode) {
          successMessage = isDraft
            ? 'Draft re-hosted successfully!'
            : 'Event re-hosted successfully!';
        } else if (isEditMode) {
          successMessage = isDraft
            ? 'Draft updated successfully!'
            : 'Event updated successfully!';
        } else {
          successMessage = isDraft
            ? 'Draft saved successfully!'
            : 'Event created successfully!';
        }
        addToast(successMessage, {
          appearance: 'success',
          autoDismiss: true,
        });
      }

      if (history) {
        const isPhone = getDeviceUser() === PHONE;
        history.push(isPhone ? '/home' : '/all-page');
      }
    } catch (error) {
      const errorMsg =
        error?.response?.data?.msgResp || error?.msgResp || 'Something went wrong!';
      if (addToast) {
        addToast(errorMsg, { appearance: 'error', autoDismiss: true });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePublish = async () => {
    await submitEvent({ isDraft: false });
  };

  const handleSaveDraft = async () => {
    await submitEvent({ isDraft: true });
  };

  const scrollToFirstError = (errors) => {
    if (!errors || Object.keys(errors).length === 0) return;
    const errorKeys = Object.keys(errors);
    let targetElement = null;

    for (const key of errorKeys) {
      const el =
        document.getElementById(`ev-${key}`) ||
        document.querySelector(`[name="${key}"]`) ||
        document.querySelector(`.error-${key}`);
      if (el) {
        targetElement = el;
        break;
      }
    }

    if (!targetElement) {
      targetElement = document.querySelector(
        '.event-hosting-container span[style*="color"], .event-hosting-container div[style*="color: #E24B4A"]'
      );
    }

    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (targetElement.focus && typeof targetElement.focus === 'function') {
        targetElement.focus({ preventScroll: true });
      }
    }
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      const isStep1Valid = await methods.trigger([
        'groupId',
        'title',
        'closingDate',
        'collectionDate',
        'description',
        'location',
        'adminCost',
        'timeFrom',
        'timeTo',
        'extraFields',
      ]);
      if (isStep1Valid) {
        nextStep();
      } else {
        setTimeout(() => scrollToFirstError(methods.formState.errors), 100);
      }
    } else if (currentStep === 2) {
      const isStep2Valid = await methods.trigger(['items']);
      if (isStep2Valid) {
        nextStep();
      } else {
        setTimeout(() => scrollToFirstError(methods.formState.errors), 100);
      }
    } else {
      await handlePublish();
    }
  };

  const handleCancel = () => {
    if (currentStep === 1) {
      clearDraftData(methods.reset, DEFAULT_EVENT_FORM_VALUES);
      history.push('/home');
    } else {
      prevStep();
    }
  };

  return {
    methods,
    currentStep,
    isSubmitting,
    handleNext,
    handlePublish,
    handleSaveDraft,
    prevStep,
    goToStep,
    handleCancel,
  };
}
